/**
 * `<TopBar>` — the single chrome row at the top of every surface.
 *
 * Layout (left → right):
 *   - Burger menu — corpus-level non-primary actions (Export soon, Exit on
 *     desktop only). The Import action is now a top-level icon button NEXT to
 *     the burger so it's discoverable at a glance, not buried in a popover.
 *   - Brand wordmark ("aletheia" — the logo mark was removed 2026-05-20).
 *   - Primary surface tabs (Editor / Search / Chat / Settings).
 *   - (flex spacer — also the drag region on desktop).
 *   - QuickFind ⌘K affordance.
 *   - Notifications bell (placeholder).
 *   - User button / Sign in link (Clerk).
 *   - On desktop only: minimize / maximize / close window controls.
 *
 * **Drag region (desktop only).** The Tauri window is `decorations: false`,
 * so the OS gives us no native title-bar to drag the window by. We mark the
 * whole `<header>` AND the flex spacer with `data-tauri-drag-region` —
 * Tauri then treats clicks on those regions as window-drag gestures. Any
 * child `<button>` / `<a>` / `<input>` clicks override (Tauri's heuristic:
 * if the click target is interactive, don't drag).
 *
 * **Why this and not a separate `<DesktopTitleBar />` above it.** The old
 * Phase D pattern stacked two bars (DesktopTitleBar + TopBar) which wasted
 * vertical space. Unified bar = ~30px more canvas + no visual hierarchy
 * confusion between "window chrome" and "app chrome."
 */

import { useEffect, useRef, useState } from 'react';
import { SignedIn, SignedOut, UserButton } from '@clerk/clerk-react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { surfaceForPath } from '@/routes';
import { useUiStore } from '@/stores';
import { useShellMountSnapshot } from './ShellMountContext';
import { isDesktopApp, isMacDesktop } from '@/desktop/isDesktopApp';
import {
  appExit,
  windowClose,
  windowMinimize,
  windowStartDragging,
  windowToggleMaximize,
} from '@/desktop/desktopIpc';

// Clerk's <SignedIn>/<SignedOut>/<UserButton> require a ClerkProvider in the tree.
// When VITE_CLERK_PUBLISHABLE_KEY is unset, AppRoot deliberately renders the shell
// WITHOUT a ClerkProvider (auth-disabled local-dev / unconfigured-deploy fallback), so
// rendering these here would throw and blank the whole app. Gate them on the same flag.
const CLERK_ENABLED = !!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

const USER_BUTTON_APPEARANCE = {
  variables: {
    colorPrimary: 'oklch(0.86 0.055 215)',
    colorBackground: 'oklch(0.180 0.004 260)',
    colorText: 'oklch(0.965 0.006 250)',
    colorTextSecondary: 'oklch(0.78 0.010 250)',
    colorNeutral: 'oklch(0.30 0.005 260)',
    fontFamily:
      '"Source Sans 3", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontSize: '14.5px',
    borderRadius: '6px',
  },
  elements: {
    avatarBox: 'topbar-user-avatar',
    userButtonPopoverCard: 'topbar-user-popover',
    userButtonPopoverActionButton: 'topbar-user-popover-action',
    userButtonPopoverActionButtonText: 'topbar-user-popover-action-text',
    userButtonPopoverFooter: 'topbar-user-popover-footer',
    userPreviewMainIdentifier: 'topbar-user-preview-main',
    userPreviewSecondaryIdentifier: 'topbar-user-preview-secondary',
  },
} as const;

export function TopBar(): JSX.Element {
  const desktop = isDesktopApp();
  // macOS draws its own native traffic-light controls (titleBarStyle: Overlay), so we don't
  // render the custom min/max/close cluster there, and we inset the left chrome so the
  // burger/brand clear the traffic lights.
  const mac = isMacDesktop();

  // Window dragging in the desktop build. v0.1.5 relied on
  // `data-tauri-drag-region` as a JSX attribute — React + Tauri's runtime
  // didn't agree on the attribute presence and drag never fired. v0.1.6
  // uses an explicit mousedown handler that calls the Rust `start_dragging`
  // command (the actual working mechanism).
  //
  // 2026-05-22 — the `data-tauri-drag-region` attribute was REMOVED: its mere
  // presence makes WebView2 (Windows) swallow the Spacebar in EVERY input in
  // the window (tauri-apps/tauri#9220) — the reported "can't type spaces in the
  // create-note title" bug. Dragging still works via the mousedown handler
  // below, and the grab cursor is class-based CSS (.app-shell--desktop), so
  // dropping the attribute costs nothing.
  const dragProps = desktop
    ? ({
        onMouseDown: (e: React.MouseEvent) => {
          // Primary button only, and never over an interactive control (the
          // button / a / input check matches Tauri's own drag-region heuristic).
          if (e.button !== 0) return;
          const target = e.target as HTMLElement;
          if (target.closest('button, a, input, select, textarea')) return;
          // Double-click toggles maximize/restore (the default Windows titlebar
          // gesture). Handled here off the click-count rather than a separate
          // `dblclick` listener, because the single-click `startDragging` below
          // begins an OS drag loop that would otherwise swallow the dblclick.
          if (e.detail === 2) {
            void windowToggleMaximize();
            return;
          }
          // Single primary click → begin an OS-level window drag.
          if (e.detail !== 1) return;
          void windowStartDragging();
        },
      } as const)
    : {};

  return (
    <header
      className={'top-bar' + (mac ? ' top-bar--mac' : '')}
      aria-label="App header"
      {...dragProps}
    >
      {/* macOS: reserve the top-left zone for the native traffic-light cluster
          (titleBarStyle: Overlay) as a real element rather than header padding,
          so it can carry its own `default` cursor — otherwise the header-wide
          `grab` cursor bleeds through under the OS pips. */}
      {mac && <span className="top-bar__mac-tl-inset" aria-hidden="true" />}

      <BurgerMenu />

      <Link to="/editor" className="top-bar__brand" aria-label="Aletheia home">
        <span className="top-bar__brand-name">aletheia</span>
      </Link>

      <SurfaceTabs />

      {/* Flex spacer — primary drag handle on desktop. Inherits the
          mousedown handler from the header; the data-tauri-drag-region
          attribute is here as the cosmetic cursor hint anchor. */}
      <span className="top-bar__drag-spacer" aria-hidden="true" {...dragProps} />

      <QuickFindButton />

      <button
        type="button"
        className="top-bar__nav-icon"
        aria-label="Notifications (none yet)"
        title="Notifications"
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <path
            d="M3 6a4 4 0 0 1 8 0v3l1 1H2l1-1Z"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M5.5 11a1.5 1.5 0 0 0 3 0"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <div className="top-bar__user">
        {CLERK_ENABLED && (
          <>
            <SignedIn>
              <UserButton
                appearance={USER_BUTTON_APPEARANCE}
                showName={false}
                afterSignOutUrl="https://aletheiapkm.com"
              />
            </SignedIn>
            <SignedOut>
              <Link to="/sign-in" className="top-bar__signin-link">
                Sign in
              </Link>
            </SignedOut>
          </>
        )}
      </div>

      {/* Desktop-only window controls — only render the CUSTOM min/max/close cluster on
          Windows/Linux. macOS uses its native traffic-light controls (top-left), so we omit
          ours there. The web build keeps the browser's native chrome. */}
      {desktop && !mac && <WindowControls />}
    </header>
  );
}

/** `<QuickFindButton>` — the ⌘K affordance. */
function QuickFindButton(): JSX.Element {
  const setOpen = useUiStore((s) => s.setCommandPaletteOpen);
  return (
    <button
      type="button"
      className="top-bar__quickfind"
      onClick={() => setOpen(true)}
      aria-label="Open quick find (⌘K)"
      title="Quick find — notes, entities, tags (⌘K)"
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
        <circle cx="6" cy="6" r="4" stroke="currentColor" strokeWidth="1.4" />
        <line x1="9" y1="9" x2="12" y2="12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      </svg>
      <span className="top-bar__quickfind-label">Quick find · notes, entities, tags</span>
      <span className="kbd">⌘K</span>
    </button>
  );
}

/**
 * `<SurfaceTabs>` — horizontal tabs in the TopBar. Reads from
 * ShellMount.getNavItems() so the registry is the source of truth.
 */
function SurfaceTabs(): JSX.Element {
  const mount = useShellMountSnapshot();
  const location = useLocation();
  const activeSurface = surfaceForPath(location.pathname);
  const items = [...mount.getNavItems()].sort((a, b) => a.order - b.order);

  return (
    <nav className="top-bar__tabs" role="tablist" aria-label="Primary">
      {items.map((item) => {
        const isActive = item.id === activeSurface;
        return (
          <NavLink
            key={item.id}
            to={item.route}
            role="tab"
            aria-selected={isActive}
            className={isActive ? 'top-bar__tab top-bar__tab--active' : 'top-bar__tab'}
            title={item.kbd ? `${item.label} (${item.kbd.toUpperCase()})` : item.label}
          >
            {item.label}
          </NavLink>
        );
      })}
    </nav>
  );
}

/**
 * `<BurgerMenu>` — three-line "more actions" popover. Now contains:
 *   - Export (soon, disabled)
 *   - Exit (DESKTOP ONLY — calls Rust app.exit(0))
 *
 * Import was promoted out; it's a top-level icon button now.
 */
function BurgerMenu(): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const desktop = isDesktopApp();

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function onExit(): void {
    setOpen(false);
    void appExit();
  }

  return (
    <div className="top-bar__burger-wrap" ref={ref}>
      <button
        type="button"
        className="top-bar__burger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Open menu"
        title="Menu"
      >
        {/* Panel-toggle glyph (facelift-v3 second pass): the design replaced
            the 3-line hamburger with a sidebar + left-chevron mark. Naked —
            strokes use currentColor so the CSS hover lights them to --accent
            with a nixie glow. */}
        <svg width="20" height="20" viewBox="0 0 22 22" fill="none" aria-hidden="true">
          <line x1="5" y1="4.5" x2="5" y2="17.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M16 6.5l-5 4.5 5 4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div className="top-bar__burger-menu" role="menu">
          <button
            type="button"
            role="menuitem"
            className="top-bar__burger-item"
            disabled
            aria-disabled="true"
            title="Export — Markdown + YAML frontmatter; ships in Phase 4"
          >
            <span className="top-bar__burger-item-label">Export</span>
            <span className="top-bar__burger-item-soon">soon</span>
          </button>

          {desktop && (
            <button
              type="button"
              role="menuitem"
              className="top-bar__burger-item top-bar__burger-item--exit"
              onClick={onExit}
              title="Quit Aletheia"
            >
              {/* macOS convention says "Quit" (and ⌘Q via the native app menu
                  also works there); Windows/Linux say "Exit". */}
              <span className="top-bar__burger-item-label">
                {isMacDesktop() ? 'Quit' : 'Exit'}
              </span>
              <span className="top-bar__burger-item-sub">Quit the desktop app</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * `<WindowControls>` — minimize / maximize / close cluster at the far right
 * of the TopBar. Replaces the standalone `<DesktopTitleBar />` from Phase D.
 * Only mounted in the desktop build (gated by the parent).
 */
function WindowControls(): JSX.Element {
  return (
    <div className="top-bar__window-controls" aria-label="Window controls">
      <button
        type="button"
        className="top-bar__window-btn"
        onClick={() => void windowMinimize()}
        aria-label="Minimize"
        title="Minimize"
      >
        <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
          <line x1="1" y1="5" x2="9" y2="5" stroke="currentColor" strokeWidth="1" />
        </svg>
      </button>
      <button
        type="button"
        className="top-bar__window-btn"
        onClick={() => void windowToggleMaximize()}
        aria-label="Maximize"
        title="Maximize"
      >
        <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
          <rect x="1.5" y="1.5" width="7" height="7" stroke="currentColor" strokeWidth="1" fill="none" />
        </svg>
      </button>
      <button
        type="button"
        className="top-bar__window-btn top-bar__window-btn--close"
        onClick={() => void windowClose()}
        aria-label="Close"
        title="Close"
      >
        <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
          <line x1="1.5" y1="1.5" x2="8.5" y2="8.5" stroke="currentColor" strokeWidth="1" />
          <line x1="8.5" y1="1.5" x2="1.5" y2="8.5" stroke="currentColor" strokeWidth="1" />
        </svg>
      </button>
    </div>
  );
}
